import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ClientService, Client } from '../../shared/services/client.service';
import { ApiService } from '../../shared/services/api.service';

@Component({
  selector: 'app-edit-client',
  templateUrl: './edit-client.component.html',
  styleUrls: ['./edit-client.component.css']
})
export class EditClientComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Form
  clientForm: FormGroup;
  
  // Loading states
  loading = false;
  loadingClient = false;
  
  // Client data
  client: Client;
  
  // Client types for dropdown
  clientTypes = [
    { value: 'individual', label: 'Individual' },
    { value: 'corporate', label: 'Corporate' },
    { value: 'government', label: 'Government' }
  ];

  // Location data properties
  allState: any[] = [];
  allDistricts: any[] = [];
  allSubDistricts: any[] = [];
  allVillage: any[] = [];
line_2: any;
line_1: any;

  constructor(
    private clientService: ClientService,
    private formBuilder: FormBuilder,
    private snackBar: MatSnackBar,
    private apiService: ApiService,
    private dialogRef: MatDialogRef<EditClientComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { client: Client }
  ) {
    this.client = data.client;
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadClientData();
    this.getstates();
    this.setupLocationChangeListeners();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    // Split client name into first and last name
    const nameParts = this.client.client_name?.split(' ') || ['', ''];
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    this.clientForm = this.formBuilder.group({
      client_first_name: [this.client.client_first_name || '', [Validators.required, Validators.minLength(2)]],
      client_last_name: [this.client.client_last_name || '', [Validators.required, Validators.minLength(2)]],
      // contact_person: [this.client.contact_person, [Validators.required, Validators.minLength(2)]],
      contact_number: [this.client.contact_number, [Validators.required, Validators.pattern(/^[\d\s\-\+\(\)]+$/)]],
      line_1: [this.client.line_1 || '', Validators.required],
      line_2: [this.client.line_2 || '', Validators.required],
      state: [this.client.state, Validators.required],
      district: [this.client.district, Validators.required],
      sub_district: [this.client.sub_district, Validators.required],
      villages: [this.client.villages, Validators.required],
      khasra_no: [this.client.khasra_no || ''],
      area: [this.client.area || ''],
      type: [this.client.type || 'individual', Validators.required]
    });
  }

  private loadClientData(): void {
    this.loadingClient = true;
    this.clientService.getClient(this.client.client_id!)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.client = response.data;
          this.initializeForm();
          this.loadLocationData();
          this.loadingClient = false;
        },
        error: (error) => {
          console.error('Error loading client:', error);
          this.snackBar.open('Failed to load client data', 'Close', { duration: 3000 });
          this.loadingClient = false;
        }
      });
  }

  private loadLocationData(): void {
    // Load dependent location data based on current client data
    // Store original values before loading options
    const originalState = this.client.state;
    const originalDistrict = this.client.district;
    const originalSubDistrict = this.client.sub_district;
    const originalVillage = this.client.villages;

    if (originalState) {
      // Load districts and restore district value after options load
      this.loadDistrictsForEdit(originalState, originalDistrict, originalSubDistrict, originalVillage);
    }
  }

  private loadDistrictsForEdit(state: string, district: string, subDistrict: string, village: string): void {
    this.apiService.getdistricts(state.toUpperCase())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.allDistricts = response || [];
          // Restore district value with case-insensitive matching
          if (district) {
            const matchingDistrict = this.allDistricts.find((d: string) => 
              d.toLowerCase() === district.toLowerCase()
            );
            if (matchingDistrict) {
              this.clientForm.patchValue({ district: matchingDistrict }, { emitEvent: false });
              this.loadSubDistrictsForEdit(state, matchingDistrict, subDistrict, village);
            }
          }
        },
        error: (error: any) => {
          console.error('Error loading districts:', error);
        }
      });
  }

  private loadSubDistrictsForEdit(state: string, district: string, subDistrict: string, village: string): void {
    this.apiService.getSubdistricts(state.toUpperCase(), district.toUpperCase())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.allSubDistricts = response || [];
          // Restore sub-district value with case-insensitive matching
          if (subDistrict) {
            const matchingSubDistrict = this.allSubDistricts.find((sd: string) => 
              sd.toLowerCase() === subDistrict.toLowerCase()
            );
            if (matchingSubDistrict) {
              this.clientForm.patchValue({ sub_district: matchingSubDistrict }, { emitEvent: false });
              this.loadVillagesForEdit(state, district, matchingSubDistrict, village);
            }
          }
        },
        error: (error: any) => {
          console.error('Error loading sub-districts:', error);
        }
      });
  }

  private loadVillagesForEdit(state: string, district: string, subDistrict: string, village: string): void {
    this.apiService.getvillages(state.toUpperCase(), district.toUpperCase(), subDistrict)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.allVillage = response || [];
          // Restore village value with case-insensitive matching
          if (village) {
            const matchingVillage = this.allVillage.find((v: string) => 
              v.toLowerCase() === village.toLowerCase()
            );
            if (matchingVillage) {
              this.clientForm.patchValue({ villages: matchingVillage }, { emitEvent: false });
            }
          }
        },
        error: (error: any) => {
          console.error('Error loading villages:', error);
        }
      });
  }

  private setupLocationChangeListeners(): void {
    // Note: We're using (selectionChange) events in the template instead of valueChanges
    // to ensure APIs are called when user manually selects from dropdown
    // This method is kept for backward compatibility but may not be needed
  }

  // Location data methods
  getstates(): void {
    this.apiService.getstates()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.allState = response || [];
        },
        error: (error: any) => {
          console.error('Error loading states:', error);
        }
      });
  }

  getdistricts(): void {
    const state = this.clientForm.get('state')?.value;
    if (state) {
      // Clear dependent dropdowns when user manually changes state
      this.allDistricts = [];
      this.allSubDistricts = [];
      this.allVillage = [];
      this.clientForm.patchValue({
        district: '',
        sub_district: '',
        villages: ''
      }, { emitEvent: false });

      this.apiService.getdistricts(state.toUpperCase())
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: any) => {
            this.allDistricts = response || [];
          },
          error: (error: any) => {
            console.error('Error loading districts:', error);
          }
        });
    }
  }

  getsubdistricts(): void {
    const state = this.clientForm.get('state')?.value;
    const district = this.clientForm.get('district')?.value;
    if (state && district) {
      // Clear dependent dropdowns
      this.allSubDistricts = [];
      this.allVillage = [];
      this.clientForm.patchValue({
        sub_district: '',
        villages: ''
      }, { emitEvent: false });

      this.apiService.getSubdistricts(state.toUpperCase(), district.toUpperCase())
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: any) => {
            this.allSubDistricts = response || [];
          },
          error: (error: any) => {
            console.error('Error loading sub-districts:', error);
          }
        });
    }
  }

  getvillages(): void {
    const state = this.clientForm.get('state')?.value;
    const district = this.clientForm.get('district')?.value;
    const subDistrict = this.clientForm.get('sub_district')?.value;
    if (state && district && subDistrict) {
      // Clear dependent dropdown
      this.allVillage = [];
      this.clientForm.patchValue({
        villages: ''
      }, { emitEvent: false });

      this.apiService.getvillages(state.toUpperCase(), district.toUpperCase(), subDistrict)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: any) => {
            this.allVillage = response || [];
          },
          error: (error: any) => {
            console.error('Error loading villages:', error);
          }
        });
    }
  }

  // Form submission
  onSubmit(): void {
    if (this.clientForm.valid) {
      this.loading = true;
      const formData = this.clientForm.value;
      formData.client_name = `${formData.client_first_name} ${formData.client_last_name}`;
      
      this.clientService.updateClient(this.client.client_id!, formData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response?.status === 'error') {
              this.showUpdateError(response.message);
              return;
            }
            this.snackBar.open('Client updated successfully', 'Close', { duration: 3000 });
            this.dialogRef.close({ success: true, data: response.data });
            this.loading = false;
          },
          error: (error) => {
            console.error('Error updating client:', error);
            this.showUpdateError(this.clientService.getApiErrorMessage(error, 'Failed to update client'));
          }
        });
    } else {
      this.markFormGroupTouched();
    }
  }

  private showUpdateError(message?: string): void {
    const errorMessage = message?.trim() || 'Failed to update client';
    this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
    if (/contact number/i.test(errorMessage)) {
      const contactCtrl = this.clientForm.get('contact_number');
      contactCtrl?.setErrors({ ...(contactCtrl.errors || {}), duplicate: true });
      contactCtrl?.markAsTouched();
    }
    this.loading = false;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  // Utility methods
  getFieldError(fieldName: string): string {
    const field = this.clientForm.get(fieldName);
    if (field && field.errors && field.touched) {
      if (field.errors['required']) return `${this.getFieldLabel(fieldName)} is required`;
      if (field.errors['minlength']) return `${this.getFieldLabel(fieldName)} must be at least ${field.errors['minlength'].requiredLength} characters`;
      if (field.errors['pattern']) return `Invalid ${this.getFieldLabel(fieldName)} format`;
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      client_first_name: 'First name',
      client_last_name: 'Last name',
      contact_person: 'Contact person',
      contact_number: 'Contact number',
      address: 'Address',
      state: 'State',
      district: 'District',
      sub_district: 'Sub-district',
      villages: 'Village',
      khasra_no: 'Khasra number',
      area: 'Area',
      type: 'Client type'
    };
    return labels[fieldName] || fieldName;
  }

  private markFormGroupTouched(): void {
    Object.keys(this.clientForm.controls).forEach(key => {
      const control = this.clientForm.get(key);
      control?.markAsTouched();
    });
  }

  // Getter methods for form controls
  get clientFirstName() { return this.clientForm.get('client_first_name'); }
  get clientLastName() { return this.clientForm.get('client_last_name'); }
  get contactPerson() { return this.clientForm.get('contact_person'); }
  get contactNumber() { return this.clientForm.get('contact_number'); }
  get address() { return this.clientForm.get('address'); }
  get state() { return this.clientForm.get('state'); }
  get district() { return this.clientForm.get('district'); }
  get subDistrict() { return this.clientForm.get('sub_district'); }
  get villages() { return this.clientForm.get('villages'); }
  get khasraNo() { return this.clientForm.get('khasra_no'); }
  get area() { return this.clientForm.get('area'); }
  get type() { return this.clientForm.get('type'); }
}

